<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import ContactFavoriteImoveisAPI from 'dashboard/api/contactFavoriteImoveis';
import FavoriteListsAPI from 'dashboard/api/favoriteLists';
import { useAlert, useTracking } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { consumeAnonymousFavoriteIntent } from 'dashboard/composables/lais/usePendingIntent';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import ConfigListDialog from '../components/ConfigListDialog.vue';
import DeleteListDialog from '../components/DeleteListDialog.vue';
import CreateEditListDialog from '../components/CreateEditListDialog.vue';
import OrganizeFavoritesDialog from '../components/OrganizeFavoritesDialog.vue';

const route = useRoute();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const { track } = useTracking();
const routeString = value => (Array.isArray(value) ? value[0] : value);
const normalizedSortMode = value =>
  ['relevance', 'recent', 'price_desc', 'price_asc', 'bairro'].includes(value)
    ? value
    : 'relevance';
const normalizedStatusFilter = value =>
  ['all', 'available', 'unavailable'].includes(value) ? value : 'all';
const normalizedBusinessContext = value =>
  ['all', 'aluguel', 'venda'].includes(value) ? value : 'all';

const list = ref(null);
const loading = ref(false);
const renaming = ref(false);
const deleting = ref(false);
const movingFavorites = ref(false);
const bulkRemoving = ref(false);
const loadingAvailableLists = ref(false);
const removingFavoriteId = ref(null);
const showDeleteDialog = ref(false);
const showConfigDialog = ref(false);
const showEditDialog = ref(false);
const showOrganizeDialog = ref(false);
const favoriteSortMode = ref(normalizedSortMode(routeString(route.query.sort)));
const favoriteStatusFilter = ref(
  normalizedStatusFilter(routeString(route.query.disponibilidade))
);
const favoriteBusinessContext = ref(
  normalizedBusinessContext(routeString(route.query.finalidade))
);
const renameInput = ref('');
const availableLists = ref([]);
const selectedFavoriteIds = ref([]);
const organizePreselectedFavoriteIds = ref([]);
const favoriteSkeletonCards = Array.from({ length: 4 }, (_, index) => index);

const sortLabels = {
  relevance: 'Relevância',
  recent: 'Mais recentes',
  price_desc: 'Maior preço',
  price_asc: 'Menor preço',
  bairro: 'Bairro',
};

const breadcrumb = computed(() => {
  if (!list.value?.contact_id) {
    return [
      { label: 'Favoritos e listas', to: { name: 'favoritos_index' } },
      { label: 'Lista' },
    ];
  }

  return [
    { label: 'Clientes', to: { name: 'lais_leads_index' } },
    {
      label: list.value.contact?.name || 'Cliente',
      to: {
        name: 'lais_lead_historico',
        params: { accountId: accountId.value, id: list.value.contact_id },
      },
    },
    {
      label: 'Favoritos e listas',
      to: {
        name: 'favoritos_index',
        params: { accountId: accountId.value },
        query: { contact_id: String(list.value.contact_id) },
      },
    },
    { label: list.value.list_name || 'Lista' },
  ];
});

const listTitle = computed(() => list.value?.list_name || 'Meus favoritos');

const headerHelper = computed(
  () =>
    'Acesse seus imóveis favoritos e os alertas criados de acordo com seus filtros.'
);

const formatDateTime = value => {
  if (!value) return 'Sem data';
  return new Date(value).toLocaleString('pt-BR');
};

const formatCurrency = value => {
  if (value == null || value === '') return null;

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(value));
};

const favoriteNumericPrice = favorite => {
  const imovel = favorite?.imovel;
  if (!imovel) return 0;

  return Number(
    imovel.finalidade === 'aluguel'
      ? imovel.valor_aluguel || 0
      : imovel.valor_venda || 0
  );
};

const favoritePrice = favorite => {
  const imovel = favorite?.imovel;
  if (!imovel) return null;

  if (imovel.finalidade === 'aluguel' && imovel.valor_aluguel) {
    return formatCurrency(imovel.valor_aluguel);
  }

  if (imovel.valor_venda) {
    return formatCurrency(imovel.valor_venda);
  }

  return null;
};

const isFavoriteAvailable = favorite =>
  favorite?.imovel?.status === 'disponivel';

const sortedFavorites = computed(() => {
  const items = [...(list.value?.favorites || [])].filter(favorite => {
    const matchesBusinessContext =
      favoriteBusinessContext.value === 'all' ||
      favorite.imovel?.finalidade === favoriteBusinessContext.value;

    return (
      matchesBusinessContext &&
      (favoriteStatusFilter.value === 'all' ||
        (favoriteStatusFilter.value === 'available' &&
          isFavoriteAvailable(favorite)) ||
        (favoriteStatusFilter.value === 'unavailable' &&
          !isFavoriteAvailable(favorite)))
    );
  });

  if (favoriteSortMode.value === 'relevance') {
    return items.sort(
      (left, right) =>
        Number(right.relevance_score || 0) - Number(left.relevance_score || 0)
    );
  }

  if (favoriteSortMode.value === 'price_desc') {
    return items.sort(
      (left, right) => favoriteNumericPrice(right) - favoriteNumericPrice(left)
    );
  }

  if (favoriteSortMode.value === 'price_asc') {
    return items.sort(
      (left, right) => favoriteNumericPrice(left) - favoriteNumericPrice(right)
    );
  }

  if (favoriteSortMode.value === 'bairro') {
    return items.sort((left, right) =>
      `${left.imovel?.bairro || ''}`.localeCompare(
        `${right.imovel?.bairro || ''}`,
        'pt-BR'
      )
    );
  }

  return items.sort(
    (left, right) =>
      new Date(right.favorited_at || 0) - new Date(left.favorited_at || 0)
  );
});

const activeFavorites = computed(() =>
  sortedFavorites.value.filter(favorite => isFavoriteAvailable(favorite))
);

const inactiveFavorites = computed(() =>
  sortedFavorites.value.filter(favorite => !isFavoriteAvailable(favorite))
);

const favoriteSections = computed(() =>
  [
    {
      key: 'available',
      title: 'Imóveis disponíveis',
      description: 'Favoritos prontos para visita, proposta ou contato.',
      favorites: activeFavorites.value,
      isInactive: false,
    },
    {
      key: 'unavailable',
      title: 'Imóveis indisponíveis',
      description: 'Imóveis que saíram da disponibilidade, mas seguem salvos.',
      favorites: inactiveFavorites.value,
      isInactive: true,
    },
  ].filter(section => section.favorites.length > 0)
);

const hasOnlyUnavailableFavorites = computed(
  () =>
    favoriteStatusFilter.value === 'all' &&
    activeFavorites.value.length === 0 &&
    inactiveFavorites.value.length > 0
);

const visibleFavoriteIds = computed(() =>
  sortedFavorites.value.map(favorite => favorite.id)
);

const selectedVisibleFavoriteIds = computed(() =>
  selectedFavoriteIds.value.filter(id => visibleFavoriteIds.value.includes(id))
);

const selectedCount = computed(() => selectedVisibleFavoriteIds.value.length);
const hasSelectedFavorites = computed(() => selectedCount.value > 0);
const allVisibleFavoritesSelected = computed(
  () =>
    visibleFavoriteIds.value.length > 0 &&
    visibleFavoriteIds.value.every(id => selectedFavoriteIds.value.includes(id))
);

const favoriteStats = computed(() => {
  const favorites = list.value?.favorites || [];
  const unavailable = favorites.filter(
    favorite => favorite.imovel?.status !== 'disponivel'
  ).length;
  const rent = favorites.filter(
    favorite => favorite.imovel?.finalidade === 'aluguel'
  ).length;
  const sale = favorites.filter(
    favorite => favorite.imovel?.finalidade === 'venda'
  ).length;
  const total = favorites.length;

  return {
    total,
    unavailable,
    available: Math.max(0, total - unavailable),
    rent,
    sale,
    selected: sortedFavorites.value.length,
  };
});

const businessContextTabs = computed(() => [
  {
    value: 'all',
    label: 'Todos',
    count: favoriteStats.value.total,
  },
  {
    value: 'aluguel',
    label: 'Alugar',
    count: favoriteStats.value.rent,
  },
  {
    value: 'venda',
    label: 'Comprar',
    count: favoriteStats.value.sale,
  },
]);

const emptyFavoritesTitle = computed(() =>
  favoriteBusinessContext.value === 'all'
    ? 'Ainda não há imóveis salvos'
    : `Ainda não há imóveis para ${favoriteBusinessContext.value === 'aluguel' ? 'alugar' : 'comprar'}`
);

const emptyFavoritesSubtitle = computed(() => {
  if (favoriteBusinessContext.value === 'all') {
    return 'Para salvar os seus imóveis favoritos aqui, ao buscar, basta tocar no ícone do coração.';
  }

  return 'Troque o filtro ou continue buscando novos imóveis para montar uma lista mais completa.';
});

const favoriteSummaryText = computed(() => {
  const availableLabel =
    favoriteStats.value.available === 1
      ? '1 imóvel disponível'
      : `${favoriteStats.value.available} imóveis disponíveis`;
  const unavailableLabel =
    favoriteStats.value.unavailable === 1
      ? '1 indisponível'
      : `${favoriteStats.value.unavailable} indisponíveis`;

  return `${availableLabel} e ${unavailableLabel}.`;
});

const tagLabel = tag =>
  ({
    novo: 'Anúncio novo',
    'preco-caiu': 'Baixou o preço',
    'alta-procura': 'Anúncio com muita procura',
    'pronto-para-visita': 'Pronto para visita',
    'oferece-financiamento': 'Financiamento',
    'com-condominio': 'Condomínio',
  })[tag] || tag;

const isFavoriteSelected = favoriteId =>
  selectedFavoriteIds.value.includes(favoriteId);

const toggleFavoriteSelection = favoriteId => {
  if (isFavoriteSelected(favoriteId)) {
    selectedFavoriteIds.value = selectedFavoriteIds.value.filter(
      id => id !== favoriteId
    );
    return;
  }

  selectedFavoriteIds.value = [...selectedFavoriteIds.value, favoriteId];
};

const clearFavoriteSelection = () => {
  selectedFavoriteIds.value = [];
};

const selectAllVisibleFavorites = () => {
  selectedFavoriteIds.value = Array.from(
    new Set([...selectedFavoriteIds.value, ...visibleFavoriteIds.value])
  );
};

const replayPendingFavoriteIntent = async currentList => {
  if (!currentList?.contact_id) return;

  const pendingFavorite = consumeAnonymousFavoriteIntent();
  const imovelId = pendingFavorite?.imovelId || pendingFavorite?.listingId;
  if (!imovelId) return;

  try {
    await ContactFavoriteImoveisAPI.create(currentList.contact_id, {
      imovel_id: imovelId,
      list_name: pendingFavorite.listName || currentList.list_name || 'default',
    });
    const { data } = await FavoriteListsAPI.show(route.params.listId);
    list.value = data;
    renameInput.value = data.list_name || '';
    track('favorite_save_intent_replayed', {
      imovel_id: imovelId,
      list_id: currentList.id,
      source: pendingFavorite.position || 'post_login',
    });
    useAlert('Imóvel salvo nos seus favoritos.');
  } catch (error) {
    const errors = error.response?.data?.errors || [];
    const alreadySaved = errors.some(message =>
      `${message}`.toLowerCase().includes('favorit')
    );
    if (!alreadySaved) {
      useAlert('Não foi possível salvar o favorito pendente.');
    }
  }
};

const loadList = async () => {
  loading.value = true;
  try {
    const { data } = await FavoriteListsAPI.show(route.params.listId);
    list.value = data;
    renameInput.value = data.list_name || '';
    await replayPendingFavoriteIntent(data);
  } catch (error) {
    list.value = null;
    useAlert(
      error.response?.data?.errors?.[0] ||
        'Não foi possível carregar esta lista.'
    );
  } finally {
    loading.value = false;
  }
};

const loadAvailableLists = async () => {
  if (!list.value?.contact_id) {
    availableLists.value = [];
    return;
  }

  loadingAvailableLists.value = true;
  try {
    const { data } = await FavoriteListsAPI.list({
      contactId: list.value.contact_id,
    });

    availableLists.value = (data.data || [])
      .map(entry => ({
        label: entry.list_name,
        value: entry.list_name,
      }))
      .filter(entry => entry.value !== list.value?.list_name);
  } catch (error) {
    availableLists.value = [];
    useAlert(
      error.response?.data?.errors?.[0] ||
        'Não foi possível carregar as outras listas deste cliente.'
    );
  } finally {
    loadingAvailableLists.value = false;
  }
};

const openImoveisForLead = () => {
  if (!list.value?.contact_id) return;

  router.push({
    name: 'imoveis_index',
    params: { accountId: accountId.value },
    query: {
      contact_id: String(list.value.contact_id),
      favorite_list: list.value.list_name,
    },
  });
};

const renameList = async () => {
  if (!list.value) return;

  if (!renameInput.value.trim()) {
    useAlert('Informe o nome atualizado da lista.');
    return;
  }

  renaming.value = true;
  try {
    const { data } = await FavoriteListsAPI.update(list.value.id, {
      name: renameInput.value,
    });

    const previousId = list.value.id;
    list.value = data;
    renameInput.value = data.list_name || '';
    showEditDialog.value = false;
    showConfigDialog.value = false;
    useAlert('Lista atualizada com sucesso.');

    if (data.id && data.id !== previousId) {
      router.replace({
        name: 'favoritos_show',
        params: { accountId: accountId.value, listId: data.id },
      });
    }
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] ||
        'Não foi possível atualizar esta lista.'
    );
  } finally {
    renaming.value = false;
  }
};

const submitRename = name => {
  renameInput.value = name;
  renameList();
};

const openOrganizeDialog = async ({ selectedOnly = false } = {}) => {
  if (!list.value?.favorites?.length) {
    useAlert(
      'Adicione favoritos nesta lista antes de abrir a organização avançada.'
    );
    return;
  }

  if (selectedOnly && !selectedCount.value) {
    useAlert('Selecione ao menos um favorito para mover.');
    return;
  }

  organizePreselectedFavoriteIds.value = selectedOnly
    ? [...selectedVisibleFavoriteIds.value]
    : [];
  await loadAvailableLists();
  showConfigDialog.value = false;
  showOrganizeDialog.value = true;
};

const removeFavorite = async favorite => {
  if (!list.value?.contact_id) return;

  const previousFavorites = [...(list.value.favorites || [])];
  removingFavoriteId.value = favorite.id;
  list.value = {
    ...list.value,
    favorites: previousFavorites.filter(item => item.id !== favorite.id),
  };

  try {
    await ContactFavoriteImoveisAPI.delete(list.value.contact_id, favorite.id);
    useAlert('Favorito removido com sucesso.');
    await loadList();

    if (!list.value) {
      router.push({
        name: 'favoritos_index',
        params: { accountId: accountId.value },
        query: {
          contact_id: String(route.query.contact_id || '') || undefined,
        },
      });
    }
  } catch (error) {
    list.value = {
      ...list.value,
      favorites: previousFavorites,
    };
    useAlert(
      error.response?.data?.errors?.[0] ||
        'Não foi possível remover este favorito.'
    );
  } finally {
    removingFavoriteId.value = null;
  }
};

const moveFavorites = async ({ favoriteIds, targetListName, sortMode }) => {
  if (!list.value?.contact_id || !favoriteIds?.length || !targetListName)
    return;

  const previousFavorites = [...(list.value.favorites || [])];
  list.value = {
    ...list.value,
    favorites: previousFavorites.filter(
      favorite => !favoriteIds.includes(favorite.id)
    ),
  };

  movingFavorites.value = true;
  try {
    await ContactFavoriteImoveisAPI.moveItems(list.value.contact_id, {
      ids: favoriteIds,
      to_list_name: targetListName,
    });

    favoriteSortMode.value = sortMode || favoriteSortMode.value;
    showOrganizeDialog.value = false;
    clearFavoriteSelection();
    await Promise.all([loadList(), loadAvailableLists()]);
    useAlert('Favoritos movidos com sucesso.');
  } catch (error) {
    list.value = {
      ...list.value,
      favorites: previousFavorites,
    };
    useAlert(
      error.response?.data?.errors?.[0] ||
        'Não foi possível mover os favoritos selecionados.'
    );
  } finally {
    movingFavorites.value = false;
  }
};

const bulkRemoveFavorites = async () => {
  if (!list.value?.contact_id || !selectedCount.value) return;

  const ids = [...selectedVisibleFavoriteIds.value];
  const previousFavorites = [...(list.value.favorites || [])];
  const listId = list.value.id;
  const contactId = list.value.contact_id;

  bulkRemoving.value = true;
  list.value = {
    ...list.value,
    favorites: previousFavorites.filter(favorite => !ids.includes(favorite.id)),
  };

  try {
    await ContactFavoriteImoveisAPI.bulkDestroy(contactId, { ids });
    clearFavoriteSelection();
    await loadList();
    track('favorites_bulk_removed', {
      count: ids.length,
      list_id: listId,
      contact_id: contactId,
    });
    useAlert('Favoritos removidos com sucesso.');
  } catch (error) {
    list.value = {
      ...list.value,
      favorites: previousFavorites,
    };
    useAlert(
      error.response?.data?.errors?.[0] ||
        'Não foi possível remover os favoritos selecionados.'
    );
  } finally {
    bulkRemoving.value = false;
  }
};

const deleteList = async () => {
  if (!list.value) return;

  deleting.value = true;
  try {
    const contactId = list.value.contact_id;
    await FavoriteListsAPI.destroy(list.value.id);
    useAlert('Lista removida com sucesso.');
    showDeleteDialog.value = false;
    showConfigDialog.value = false;
    router.push({
      name: 'favoritos_index',
      params: { accountId: accountId.value },
      query: contactId ? { contact_id: String(contactId) } : {},
    });
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] ||
        'Não foi possível remover esta lista.'
    );
  } finally {
    deleting.value = false;
  }
};

const openImovel = favorite => {
  if (!favorite?.imovel_id) return;

  router.push({
    name: 'imovel_show',
    params: { accountId: accountId.value, id: favorite.imovel_id },
    query: list.value?.contact_id
      ? { contact_id: String(list.value.contact_id) }
      : {},
  });
};

const compactQuery = query =>
  Object.fromEntries(
    Object.entries(query).filter(([, value]) => value != null && value !== '')
  );

const querySignature = query =>
  Object.entries(query)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${Array.isArray(value) ? value[0] : value}`)
    .join('|');

const syncFavoriteListRouteState = () => {
  const nextQuery = compactQuery({
    ...route.query,
    sort:
      favoriteSortMode.value === 'relevance'
        ? undefined
        : favoriteSortMode.value,
    disponibilidade:
      favoriteStatusFilter.value === 'all'
        ? undefined
        : favoriteStatusFilter.value,
    finalidade:
      favoriteBusinessContext.value === 'all'
        ? undefined
        : favoriteBusinessContext.value,
  });

  if (querySignature(route.query) === querySignature(nextQuery)) return;

  router.replace({ query: nextQuery }).catch(() => {});
};

watch(
  [favoriteSortMode, favoriteStatusFilter, favoriteBusinessContext],
  syncFavoriteListRouteState
);

watch([favoriteStatusFilter, favoriteBusinessContext], clearFavoriteSelection);

watch(
  () => route.params.listId,
  () => {
    clearFavoriteSelection();
    loadList();
  },
  { immediate: true }
);
</script>

<template>
  <section class="flex h-full flex-col overflow-hidden bg-[#fcfaf6]">
    <LaisHeader
      :title="listTitle"
      :helper="headerHelper"
      :breadcrumb="breadcrumb"
    >
      <template #actions>
        <LaisPrimaryButton
          v-if="list"
          icon="i-lucide-settings-2"
          @click="showConfigDialog = true"
        >
          Configurar lista
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="list?.contact_id"
          icon="i-lucide-heart-plus"
          variant="ghost"
          @click="openImoveisForLead"
        >
          Adicionar imóveis
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <div class="flex-1 overflow-auto px-4 py-4 sm:px-6 lg:px-10 lg:py-6">
      <div
        v-if="loading"
        class="grid gap-4 md:grid-cols-2 xl:grid-cols-3"
        aria-label="Carregando lista"
      >
        <div
          v-for="card in favoriteSkeletonCards"
          :key="card"
          class="animate-pulse overflow-hidden rounded-lais border border-lais-border bg-white"
        >
          <div class="h-32 bg-[#f2eef8]" />
          <div class="space-y-3 p-4">
            <div class="h-4 w-3/4 rounded bg-[#ece6f5]" />
            <div class="h-3 w-1/2 rounded bg-[#f1edf7]" />
            <div class="h-8 w-full rounded bg-[#f7f3fb]" />
          </div>
        </div>
      </div>

      <div
        v-else-if="list"
        class="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]"
      >
        <div class="grid gap-5">
          <LaisCard title="Favoritos desta lista">
            <template #titleAction>
              <div class="flex flex-wrap items-center justify-end gap-2">
                <select
                  v-model="favoriteSortMode"
                  class="h-9 rounded-lais border border-lais-border bg-white px-3 text-[12px] font-semibold text-lais-text focus:border-lais-primary focus:outline-none focus:ring-2 focus:ring-lais-primary-50"
                  aria-label="Ordenar favoritos"
                >
                  <option value="relevance">Relevância</option>
                  <option value="recent">Mais recentes</option>
                  <option value="price_desc">Maior preço</option>
                  <option value="price_asc">Menor preço</option>
                  <option value="bairro">Bairro</option>
                </select>
                <select
                  v-model="favoriteStatusFilter"
                  class="h-9 rounded-lais border border-lais-border bg-white px-3 text-[12px] font-semibold text-lais-text focus:border-lais-primary focus:outline-none focus:ring-2 focus:ring-lais-primary-50"
                  aria-label="Filtrar favoritos por disponibilidade"
                >
                  <option value="all">Todos</option>
                  <option value="available">Disponíveis</option>
                  <option value="unavailable">Indisponíveis</option>
                </select>
              </div>
            </template>

            <div
              v-if="!list.favorites || list.favorites.length === 0"
              class="space-y-4 text-[13px] leading-6 text-lais-text-muted"
            >
              <p class="text-[16px] font-semibold text-lais-text">
                Ainda não há imóveis salvos
              </p>
              <p>
                Para salvar os seus imóveis favoritos aqui, ao buscar, basta
                tocar no ícone do coração.
              </p>
              <LaisPrimaryButton
                icon="i-lucide-heart-plus"
                @click="openImoveisForLead"
              >
                Adicionar imóveis em lote
              </LaisPrimaryButton>
            </div>

            <div v-else class="space-y-4">
              <div
                class="flex flex-wrap items-center gap-2 rounded-lais border border-lais-border bg-white p-1"
                aria-label="Filtrar favoritos por finalidade"
              >
                <button
                  v-for="tab in businessContextTabs"
                  :key="tab.value"
                  type="button"
                  class="inline-flex min-h-[40px] flex-1 items-center justify-center gap-2 rounded-[6px] px-3 py-2 text-[13px] font-semibold transition-all sm:flex-none"
                  :class="
                    favoriteBusinessContext === tab.value
                      ? 'bg-lais-primary text-white shadow-sm'
                      : 'text-lais-text-muted hover:bg-lais-bg-soft hover:text-lais-text'
                  "
                  @click="favoriteBusinessContext = tab.value"
                >
                  <span>{{ tab.label }}</span>
                  <span
                    class="rounded-full px-2 py-0.5 text-[11px]"
                    :class="
                      favoriteBusinessContext === tab.value
                        ? 'bg-white/20 text-white'
                        : 'bg-lais-bg-soft text-lais-text-muted'
                    "
                  >
                    {{ tab.count }}
                  </span>
                </button>
              </div>

              <div class="grid gap-3 sm:grid-cols-4">
                <div
                  class="rounded-lais border border-lais-border-soft bg-lais-bg-soft px-4 py-3"
                >
                  <p
                    class="text-[11px] font-semibold uppercase text-lais-text-muted"
                  >
                    Favoritos
                  </p>
                  <p class="mt-1 text-[20px] font-semibold text-lais-text">
                    {{ favoriteStats.total }}
                  </p>
                </div>
                <div
                  class="rounded-lais border border-lais-border-soft bg-lais-bg-soft px-4 py-3"
                >
                  <p
                    class="text-[11px] font-semibold uppercase text-lais-text-muted"
                  >
                    Disponíveis
                  </p>
                  <p class="mt-1 text-[20px] font-semibold text-lais-text">
                    {{ favoriteStats.available }}
                  </p>
                </div>
                <div
                  class="rounded-lais border border-lais-border-soft bg-lais-bg-soft px-4 py-3"
                >
                  <p
                    class="text-[11px] font-semibold uppercase text-lais-text-muted"
                  >
                    Indisponíveis
                  </p>
                  <p class="mt-1 text-[20px] font-semibold text-lais-text">
                    {{ favoriteStats.unavailable }}
                  </p>
                </div>
                <div
                  class="rounded-lais border border-lais-border-soft bg-lais-bg-soft px-4 py-3"
                >
                  <p
                    class="text-[11px] font-semibold uppercase text-lais-text-muted"
                  >
                    Exibidos
                  </p>
                  <p class="mt-1 text-[20px] font-semibold text-lais-text">
                    {{ favoriteStats.selected }}
                  </p>
                </div>
              </div>

              <div
                v-if="hasSelectedFavorites"
                class="sticky top-0 z-20 flex flex-wrap items-center gap-2 rounded-lais border border-lais-primary-50 bg-white/95 px-4 py-3 shadow-lais-soft backdrop-blur"
              >
                <p class="mr-auto text-[13px] font-semibold text-lais-text">
                  {{ selectedCount }} selecionados
                </p>
                <button
                  type="button"
                  class="inline-flex min-h-[36px] items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 text-[12px] font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link"
                  @click="clearFavoriteSelection"
                >
                  Limpar seleção
                </button>
                <button
                  type="button"
                  class="inline-flex min-h-[36px] items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 text-[12px] font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="allVisibleFavoritesSelected"
                  @click="selectAllVisibleFavorites"
                >
                  Selecionar todos os visíveis
                </button>
                <button
                  type="button"
                  class="inline-flex min-h-[36px] items-center gap-2 rounded-lais bg-lais-primary px-3 py-2 text-[12px] font-semibold text-white transition-all hover:bg-[#3c236c]"
                  @click="openOrganizeDialog({ selectedOnly: true })"
                >
                  <span class="i-lucide-arrow-right-left size-4" />
                  Mover selecionados
                </button>
                <button
                  type="button"
                  class="inline-flex min-h-[36px] items-center gap-2 rounded-lais border border-[#fecdca] bg-white px-3 py-2 text-[12px] font-semibold text-[#b42318] transition-all hover:bg-[#fffbfa] disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="bulkRemoving"
                  @click="bulkRemoveFavorites"
                >
                  <span
                    class="size-4"
                    :class="
                      bulkRemoving
                        ? 'i-lucide-loader-circle animate-spin'
                        : 'i-lucide-trash-2'
                    "
                  />
                  Remover selecionados
                </button>
              </div>

              <div
                v-if="sortedFavorites.length === 0"
                class="space-y-2 rounded-lais border border-dashed border-lais-border bg-white px-4 py-6"
              >
                <p class="text-[15px] font-semibold text-lais-text">
                  {{ emptyFavoritesTitle }}
                </p>
                <p class="text-[13px] leading-6 text-lais-text-muted">
                  {{ emptyFavoritesSubtitle }}
                </p>
              </div>

              <div
                v-else-if="hasOnlyUnavailableFavorites"
                class="rounded-lais border border-[#fedf89] bg-[#fffaeb] px-4 py-3 text-[13px] leading-6 text-[#93370d]"
              >
                Todos os imóveis desta lista estão indisponíveis no momento.
              </div>

              <div v-if="sortedFavorites.length > 0" class="space-y-6">
                <section
                  v-for="section in favoriteSections"
                  :key="section.key"
                  class="space-y-3"
                >
                  <div class="flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <h3 class="text-[15px] font-semibold text-lais-text">
                        {{ section.title }}
                      </h3>
                      <p class="mt-1 text-[12px] text-lais-text-muted">
                        {{ section.description }}
                      </p>
                    </div>
                    <span
                      class="rounded-full bg-lais-bg-soft px-3 py-1 text-[12px] font-semibold text-lais-text-muted"
                    >
                      {{ section.favorites.length }}
                    </span>
                  </div>

                  <div class="grid gap-4">
                    <article
                      v-for="favorite in section.favorites"
                      :key="favorite.id"
                      class="overflow-hidden rounded-lais border border-lais-border bg-white transition-all"
                      :class="section.isInactive ? 'opacity-80' : ''"
                    >
                      <div class="flex flex-col gap-4 p-4 md:flex-row">
                        <label
                          class="flex items-start gap-2 text-[12px] font-semibold text-lais-text-muted md:w-7"
                        >
                          <input
                            type="checkbox"
                            class="mt-1 size-4 accent-[#49317a]"
                            :checked="isFavoriteSelected(favorite.id)"
                            @click.stop
                            @change.stop="toggleFavoriteSelection(favorite.id)"
                          />
                          <span class="sr-only">Selecionar favorito</span>
                        </label>

                        <div
                          class="flex h-[124px] w-full shrink-0 items-center justify-center overflow-hidden rounded-lais bg-[#f4ecff] md:w-[168px]"
                        >
                          <img
                            v-if="favorite.imovel?.thumbnail"
                            :src="favorite.imovel.thumbnail"
                            :alt="
                              favorite.imovel?.titulo || 'Imóvel favoritado'
                            "
                            class="h-full w-full object-cover"
                          />
                          <span
                            v-else
                            class="i-lucide-heart size-8 text-[#d4546a]"
                            aria-hidden="true"
                          />
                        </div>

                        <div class="flex min-w-0 flex-1 flex-col">
                          <div class="flex items-start justify-between gap-3">
                            <div class="min-w-0">
                              <p
                                class="text-[15px] font-semibold text-lais-text"
                              >
                                {{
                                  favorite.imovel?.titulo ||
                                  `Imóvel #${favorite.imovel_id}`
                                }}
                              </p>
                              <p class="mt-1 text-[12px] text-lais-text-muted">
                                {{
                                  [
                                    favorite.imovel?.bairro,
                                    favorite.imovel?.cidade,
                                    favorite.imovel?.tipo,
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')
                                }}
                              </p>
                              <p
                                v-if="favoritePrice(favorite)"
                                class="mt-2 text-[13px] font-semibold text-lais-text"
                              >
                                {{ favoritePrice(favorite) }}
                              </p>
                            </div>
                            <span
                              class="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                              :class="
                                favorite.imovel?.status === 'disponivel'
                                  ? 'bg-[#ecfdf3] text-[#027a48]'
                                  : 'bg-[#fff6ed] text-[#c4320a]'
                              "
                            >
                              {{
                                favorite.imovel?.status === 'disponivel'
                                  ? 'Disponível'
                                  : favorite.imovel?.status || 'Indisponível'
                              }}
                            </span>
                          </div>

                          <div
                            v-if="favorite.imovel?.listing_tags?.length"
                            class="mt-3 flex flex-wrap gap-2"
                          >
                            <span
                              v-for="tag in favorite.imovel.listing_tags"
                              :key="`${favorite.id}-${tag}`"
                              class="rounded-full bg-lais-primary-50 px-2.5 py-1 text-[11px] font-semibold text-lais-primary"
                            >
                              {{ tagLabel(tag) }}
                            </span>
                          </div>

                          <p
                            class="mt-3 text-[12px] leading-5 text-lais-text-muted"
                          >
                            Favoritado em
                            {{ formatDateTime(favorite.favorited_at) }}
                          </p>
                          <p
                            v-if="favorite.note"
                            class="mt-2 rounded-lais border border-lais-border bg-[#fffdf8] px-3 py-2 text-[12px] text-lais-text-muted"
                          >
                            {{ favorite.note }}
                          </p>

                          <div class="mt-auto flex flex-wrap gap-2 pt-4">
                            <button
                              type="button"
                              class="inline-flex items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 text-[13px] font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link"
                              @click="openImovel(favorite)"
                            >
                              <span class="i-lucide-building size-4" />
                              Abrir imóvel
                            </button>
                            <button
                              type="button"
                              class="inline-flex items-center gap-2 rounded-lais border border-[#f1c9cf] bg-white px-3 py-2 text-[13px] font-semibold text-[#b42318] transition-all hover:bg-[#fff5f6]"
                              :disabled="removingFavoriteId === favorite.id"
                              @click="removeFavorite(favorite)"
                            >
                              <span class="i-lucide-heart-off size-4" />
                              Remover favorito
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  </div>
                </section>
              </div>
            </div>
          </LaisCard>
        </div>

        <div class="grid gap-5">
          <LaisCard title="Configurar lista">
            <div class="space-y-4 text-[13px] leading-6 text-lais-text-muted">
              <p>
                {{ favoriteSummaryText }}
              </p>
              <p>
                Ordenação atual:
                <strong class="text-lais-text">
                  {{ sortLabels[favoriteSortMode] || 'Relevância' }}
                </strong>
              </p>

              <div class="flex flex-wrap gap-2">
                <LaisPrimaryButton
                  icon="i-lucide-heart-plus"
                  @click="openImoveisForLead"
                >
                  Adicionar imóveis
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  icon="i-lucide-settings-2"
                  variant="ghost"
                  @click="showConfigDialog = true"
                >
                  Abrir ações da lista
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  icon="i-lucide-arrow-right-left"
                  variant="ghost"
                  @click="openOrganizeDialog()"
                >
                  Organizar favoritos
                </LaisPrimaryButton>
              </div>
            </div>
          </LaisCard>

          <LaisCard title="Resumo rápido">
            <div class="space-y-3 text-[13px] leading-6 text-lais-text-muted">
              <p>
                Cliente:
                <strong class="text-lais-text">{{
                  list.contact?.name || 'Cliente sem nome'
                }}</strong>
              </p>
              <p>Total de favoritos: {{ list.total_favorites || 0 }}</p>
              <p>
                Última atividade: {{ formatDateTime(list.last_favorited_at) }}
              </p>
              <div class="flex flex-wrap gap-2">
                <RouterLink
                  v-if="list.contact_id"
                  :to="{
                    name: 'lais_lead_historico',
                    params: { accountId, id: list.contact_id },
                  }"
                  class="inline-flex items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link"
                >
                  <span class="i-lucide-history size-4" />
                  Histórico do cliente
                </RouterLink>
                <RouterLink
                  :to="{
                    name: 'favoritos_index',
                    params: { accountId },
                    query: list.contact_id
                      ? { contact_id: String(list.contact_id) }
                      : {},
                  }"
                  class="inline-flex items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link"
                >
                  <span class="i-lucide-arrow-left size-4" />
                  Voltar para listas
                </RouterLink>
              </div>
            </div>
          </LaisCard>
        </div>
      </div>
    </div>
  </section>

  <ConfigListDialog
    :show="showConfigDialog"
    :list-name="list?.list_name"
    :total-favorites="list?.total_favorites || 0"
    @close="showConfigDialog = false"
    @edit="
      showConfigDialog = false;
      showEditDialog = true;
    "
    @organize="openOrganizeDialog"
    @delete="
      showConfigDialog = false;
      showDeleteDialog = true;
    "
  />

  <CreateEditListDialog
    mode="edit"
    :show="showEditDialog"
    :current-name="list?.list_name"
    :is-loading="renaming"
    @close="showEditDialog = false"
    @submit="submitRename"
  />

  <OrganizeFavoritesDialog
    :show="showOrganizeDialog"
    :list-name="list?.list_name"
    :favorites="list?.favorites || []"
    :available-lists="availableLists"
    :preselected-favorite-ids="organizePreselectedFavoriteIds"
    :is-loading="movingFavorites || loadingAvailableLists"
    @close="showOrganizeDialog = false"
    @submit="moveFavorites"
  />

  <DeleteListDialog
    :show="showDeleteDialog"
    :list-name="list?.list_name"
    :total-favorites="list?.total_favorites || 0"
    :is-loading="deleting"
    @close="showDeleteDialog = false"
    @confirm="deleteList"
  />
</template>
